import React, { useEffect, useId, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HistoryToggleOffRoundedIcon from '@mui/icons-material/HistoryToggleOffRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PortfolioChooser from './PortfolioChooser';
import SavedPortfolioDialogs from './SavedPortfolioDialogs';
import useSavedPortfolioManagement from './useSavedPortfolioManagement';
import {
    formatBaselineLabel,
    formatOptimizerContextSummary,
    formatRelativeAge,
    getOptimizerContextPresentation,
    getPortfolioStaleTooltip,
    optimizerPortfolioNeedsRefresh,
    optimizerPortfolioStatusTags,
} from './optimizerPortfolioPresentation';

const stopMenuEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
};

function PortfolioIndicators({ item, draftStatus, draftFreshness, includeModified = false }) {
    const presentation = item?.type === 'opt' ? getOptimizerContextPresentation(item) : null;
    const attention = presentation && (!presentation.historicalAvailable || presentation.restorationRequired);
    const attentionReason = presentation?.restorationRequired
        ? `${presentation.missingSecurityCount} saved securit${presentation.missingSecurityCount === 1 ? 'y is' : 'ies are'} missing from your Watchlist.`
        : 'The saved optimization is unavailable.';
    const stale = optimizerPortfolioNeedsRefresh(item, draftStatus, draftFreshness)
        || (includeModified && presentation?.materializationState === 'modified');
    return <>
        {attention && <Tooltip title={attentionReason}>
            <Box component="span" role="img" aria-label={`Needs attention: ${attentionReason}`} tabIndex={0}
                sx={{ display: 'inline-flex', flexShrink: 0 }}>
                <PriorityHighRoundedIcon sx={{ fontSize: 18, color: 'error.main', opacity: 0.8 }} />
            </Box>
        </Tooltip>}
        {stale && <Tooltip title={getPortfolioStaleTooltip(item, draftStatus, draftFreshness)}>
            <HistoryToggleOffRoundedIcon sx={{ fontSize: 18, color: 'warning.main', opacity: 0.8, flexShrink: 0 }} />
        </Tooltip>}
    </>;
}

function PortfolioActionStatus({ status, item, onDetails }) {
    if (!status) return null;
    const pending = status.type === 'pending';
    const success = status.type === 'success';
    const error = status.type === 'error';
    if (!pending && !success && !error) return null;
    return <Box sx={{ display: 'flex', alignItems: 'center', gap: pending ? 0.5 : 0.35,
        color: pending ? 'primary.main' : success ? 'success.main' : 'error.main', opacity: 0.85 }}>
        {pending ? <CircularProgress size={13} thickness={6} color="inherit" />
            : success ? <CheckCircleRoundedIcon sx={{ fontSize: 15 }} /> : <ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />}
        <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 500, opacity: 0.82 }}>{status.label}</Typography>
        {error && <Button size="small" onMouseDown={stopMenuEvent} onClick={(event) => {
            stopMenuEvent(event);
            onDetails({ portfolioName: item.value, portfolioType: item.type, details: status.details });
        }} sx={{ minWidth: 'auto', p: 0, lineHeight: 1, fontSize: '0.72rem', color: 'inherit', textTransform: 'none' }}>(i)</Button>}
    </Box>;
}

// Preserve the existing tab-facing API while keeping selection presentation and
// management operations on separate, independently testable surfaces.
export default function OptimizerPortfolioDropdown(props) {
    const {
        availableBetaSources = null,
        compact = false,
        hideTitle = false,
        onSyncPosSizingDraft = null,
        performanceSources = null,
        posSizingFreshnessSummary = null,
        posSizingSyncDisabled = false,
        posSizingSyncInProgress = false,
        posSizingSyncStatus = null,
        restrictedDeletePortfolioNames,
        unsavedOptimizerRun,
    } = props;
    const { chooser, dialogs, actions } = useSavedPortfolioManagement(props);
    const { visiblePortfolios, selectedPortfolio, selectedPortfolioIdValue, loading,
        loadingMessage, menuOpen, refreshStatuses, refreshingPortfolioIds } = chooser;
    const uniqueLabelId = useId();
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const intervalId = window.setInterval(() => setNowMs(Date.now()), 60_000);
        return () => window.clearInterval(intervalId);
    }, []);

    const indicators = (item, includeModified = false) => <PortfolioIndicators item={item}
        draftStatus={posSizingSyncStatus} draftFreshness={posSizingFreshnessSummary} includeModified={includeModified} />;
    const savedOptions = visiblePortfolios.map((item) => {
        const canDelete = !performanceSources && !restrictedDeletePortfolioNames.includes(item.value)
            && !item.optimizerMutationBlocked;
        const canSyncDraft = item.type === 'wi' && Boolean(onSyncPosSizingDraft);
        const needsSync = item.type === 'wi' && optimizerPortfolioNeedsRefresh(item, posSizingSyncStatus, posSizingFreshnessSummary);
        const checking = item.type === 'wi' && Boolean(posSizingFreshnessSummary?.loading);
        const refreshing = Boolean(refreshingPortfolioIds[item.id]);
        const syncLabel = checking ? 'Checking Position Sizing freshness'
            : posSizingSyncDisabled ? 'Wait for the current Watchlist update to finish'
                : needsSync ? 'Sync position sizing draft' : 'Position Sizing draft is already current';
        const label = performanceSources?.portfolioLabel(item) ?? item.value;
        return {
            value: String(item.id),
            variant: 'portfolio',
            label,
            title: performanceSources ? label : undefined,
            description: item.type === 'wi'
                ? `Draft • Saved ${formatOptimizerContextSummary(item, availableBetaSources)}`
                : `Based on ${formatBaselineLabel(item.baselineSourceMode)} Portfolio`,
            indicators: indicators(item),
            metadata: item.lastRefreshedAt
                ? `${item.type === 'opt' ? 'Saved' : 'Updated'} ${formatRelativeAge(item.lastRefreshedAt, nowMs)}`
                : 'Save time unavailable',
            status: <PortfolioActionStatus status={refreshStatuses[item.id]} item={item} onDetails={actions.showRefreshIssue} />,
            badges: optimizerPortfolioStatusTags(item).filter((tag) => tag.color !== 'error').map((tag) => (
                <Chip key={tag.key} size="small" variant="outlined" color={tag.color} label={tag.label}
                    sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }} />
            )),
            actions: <>
                {canSyncDraft && <Tooltip title={syncLabel}><span>
                    <IconButton edge="end" size="small" aria-label={syncLabel}
                        disabled={loading || refreshing || checking || posSizingSyncDisabled || !needsSync}
                        onMouseDown={stopMenuEvent} onClick={(event) => { stopMenuEvent(event); void actions.syncDraft(item); }}
                        color="primary" sx={{ width: 28, height: 28 }}>
                        {refreshing ? <CircularProgress size={16} thickness={6} color="inherit" /> : <RefreshRoundedIcon fontSize="small" />}
                    </IconButton>
                </span></Tooltip>}
                {!(performanceSources && item.type === 'wi') && <Tooltip title="View portfolio details"><span>
                    <IconButton edge="end" size="small" aria-label={`View ${item.value} details`} disabled={loading}
                        onMouseDown={stopMenuEvent} onClick={(event) => { stopMenuEvent(event); actions.viewDetails(item); }}
                        color="primary" sx={{ width: 28, height: 28 }}><InfoOutlinedIcon fontSize="small" /></IconButton>
                </span></Tooltip>}
                {canDelete && <Tooltip title="Delete saved portfolio"><span>
                    <IconButton edge="end" size="small" aria-label={`Delete ${item.value}`} disabled={loading}
                        onMouseDown={stopMenuEvent} onClick={(event) => { stopMenuEvent(event); actions.requestDelete(item); }}
                        color="primary" sx={{ width: 28, height: 28, opacity: 0.7, '&:hover': { bgcolor: 'action.hover' } }}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </span></Tooltip>}
            </>,
        };
    });
    const sourceOptions = (group) => performanceSources.entries.filter((entry) => entry.group === group)
        .map((entry) => ({ ...entry, variant: 'source', title: entry.label, reselectable: true }));
    const groups = performanceSources ? [
        { id: 'constant', label: 'Constant portfolios', options: [
            ...sourceOptions('constant'), ...savedOptions,
            ...(performanceSources.missingSelection ? [{ ...performanceSources.missingSelection,
                disabled: true, countOverflow: false, variant: 'source' }] : []),
        ] },
        { id: 'execution', label: 'Execution sources', options: sourceOptions('execution') },
    ] : [{ id: 'saved', options: savedOptions }];
    const selectedLabel = selectedPortfolio
        ? performanceSources?.selectedLabel ?? selectedPortfolio.value
        : performanceSources?.selectedLabel ?? (props.dropdownDisplayValue || '');

    return <>
        <PortfolioChooser label={performanceSources?.label ?? (compact ? 'Portfolio' : 'Load Saved Portfolio')}
            labelId={performanceSources ? uniqueLabelId : 'objective-select-label'}
            value={selectedPortfolioIdValue} selectedLabel={selectedLabel} selectedTitle={performanceSources?.selectedLabel}
            selectedIndicators={selectedPortfolio ? indicators(selectedPortfolio, true) : null}
            groups={groups} emptyLabel={performanceSources ? null : 'No data available'}
            open={menuOpen} onOpen={actions.openMenu} onClose={actions.closeMenu}
            onSelect={actions.select} onReselect={actions.selectEntry}
            hideTitle={hideTitle} compact={compact} unsaved={unsavedOptimizerRun} disabled={performanceSources?.disabled}
            helperText={loading && loadingMessage ? loadingMessage : null} />
        <SavedPortfolioDialogs model={dialogs} actions={actions} loading={loading} nowMs={nowMs}
            allowDelete={!performanceSources} restrictedDeletePortfolioNames={restrictedDeletePortfolioNames}
            draft={{ posSizingFreshnessSummary, onSyncPosSizingDraft, posSizingSyncDisabled, posSizingSyncInProgress }} />
    </>;
}
